import React from "react";
import { createPortal } from "react-dom";
import { useEffect, useState, useLayoutEffect, useRef } from "react";

type OverflowChipsProps<T> = {
	items: T[];
	rowIndex: number;
	rowCount: number;
	maxVisible?: number;
	maxWidthClass?: string;
	getKey: (item: T) => string | number;
	chip: (item: T) => React.ReactNode;
};

type Pos = { top: number; left: number; placement: "top" | "bottom" };

function clamp(n: number, min: number, max: number) {
	return Math.max(min, Math.min(max, n));
}

export default function OverflowChips<T>({
	items,
	rowIndex,
	rowCount,
	maxVisible = 3,
	maxWidthClass = "",
	getKey,
	chip,
}: OverflowChipsProps<T>) {
	const rootRef = useRef<HTMLDivElement | null>(null);
	const popupRef = useRef<HTMLDivElement | null>(null);

	const [popupOpen, setPopupOpen] = useState(false);
	const [pos, setPos] = useState<Pos | null>(null);

	const hasOverflow = items.length > maxVisible;
	const openUpByRow = rowIndex >= rowCount - 2;

	const seedPosition = () => {
		const root = rootRef.current;
		if (!root) return;

		const r = root.getBoundingClientRect();
		const gap = 8;

		setPos({
			top: openUpByRow ? r.top - gap : r.bottom + gap,
			left: clamp(r.left, 8, window.innerWidth - 8),
			placement: openUpByRow ? "top" : "bottom",
		});
	};

	const computePosition = () => {
		const root = rootRef.current;
		const pop = popupRef.current;
		if (!root || !pop) return;

		const r = root.getBoundingClientRect();
		const popRect = pop.getBoundingClientRect();

		const gap = 8;
		const viewportW = window.innerWidth;
		const viewportH = window.innerHeight;

		const spaceBelow = viewportH - r.bottom;
		const spaceAbove = r.top;

		let placement: "top" | "bottom" = openUpByRow ? "top" : "bottom";

		if (
			placement === "bottom" &&
			spaceBelow < popRect.height + gap &&
			spaceAbove > spaceBelow
		) {
			placement = "top";
		}
		if (placement === "top" && spaceAbove < popRect.height + gap && spaceBelow > spaceAbove) {
			placement = "bottom";
		}

		const top = placement === "bottom" ? r.bottom + gap : r.top - popRect.height - gap;
		const left = clamp(r.left, 8, viewportW - popRect.width - 8);

		setPos({ top, left, placement });
	};

	useLayoutEffect(() => {
		if (!popupOpen) return;
		// after portal mounts, refine using popover size
		computePosition();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [popupOpen, rowIndex, rowCount, items.length]);

	useEffect(() => {
		if (!popupOpen) return;

		const onScrollOrResize = () => computePosition();
		window.addEventListener("scroll", onScrollOrResize, true);
		window.addEventListener("resize", onScrollOrResize);

		return () => {
			window.removeEventListener("scroll", onScrollOrResize, true);
			window.removeEventListener("resize", onScrollOrResize);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [popupOpen]);

	return (
		<div
			ref={rootRef}
			className={`relative flex flex-wrap gap-1 sm:gap-1.5 md:gap-2 items-center ${maxWidthClass}`}
			onMouseEnter={() => {
				if (!hasOverflow) return;
				seedPosition(); // ✅ prevents first-frame top-left
				setPopupOpen(true);
			}}
			onMouseLeave={() => setPopupOpen(false)}>
			{items.slice(0, maxVisible).map((item) => (
				<React.Fragment key={getKey(item)}>{chip(item)}</React.Fragment>
			))}

			{hasOverflow && (
				<span className="text-xs text-primary-800 cursor-default">
					+{items.length - maxVisible}
				</span>
			)}

			{popupOpen &&
				hasOverflow &&
				createPortal(
					<div
						ref={popupRef}
						style={{
							position: "fixed",
							top: pos?.top ?? 0,
							left: pos?.left ?? 0,
							visibility: pos ? "visible" : "hidden",
						}}
						className={`z-[9999] w-max max-w-[420px] rounded-lg border bg-white py-2 px-4 shadow-lg
              pointer-events-none transition-opacity duration-150 ${pos ? "opacity-100" : "opacity-0"}`}>
						<div className="flex flex-wrap gap-1 sm:gap-1.5 md:gap-2">
							{items.map((item) => (
								<React.Fragment key={getKey(item)}>{chip(item)}</React.Fragment>
							))}
						</div>
					</div>,
					document.body
				)}
		</div>
	);
}
